import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from "../../config";
import {
    Activity,
    AlertCircle,
    ArrowDown,
    ArrowUp,
    BarChart3,
    BookOpen,
    CheckCircle,
    Clock,
    Eye,
    Filter,
    Lightbulb,
    Target,
    TrendingUp,
    User,
    Users,
    X
} from 'lucide-react';

// Constants - matching TeacherView.js
const colors = {
    orange: "#D67C2C",
    blue: "#2173B7",
    green: "#22c55e",
    purple: "#a855f7"
};

const CompetencyManagement = () => {
    // State management
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Notification state - matching TeacherView.js
    const [notification, setNotification] = useState(null);

    // Data state
    const [overview, setOverview] = useState(null);
    const [classMeters, setClassMeters] = useState([]);
    const [teacherOverview, setTeacherOverview] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [selectedView, setSelectedView] = useState('overview'); // overview, classes, teachers, analytics

    // Filter state
    const [filters, setFilters] = useState({
        grade: '',
        teacher: '',
        search: ''
    });

    // Competency Gaps table filter state
    const [gapsFilters, setGapsFilters] = useState({
        domain: '',
        status: ''
    });

    // Modal state
    const [selectedClass, setSelectedClass] = useState(null);
    const [showClassModal, setShowClassModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showStudentModal, setShowStudentModal] = useState(false);

    // API base URL imported from config

    useEffect(() => {
        fetchData();
    }, [selectedView, filters]);

    // API helper function
    const apiCall = async (endpoint, options = {}) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}${endpoint} `, {
            headers: {
                'Authorization': `Bearer ${token} `,
                'Content-Type': 'application/json',
                ...options.headers
            },
            ...options
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP ${response.status} `);
        }

        return response.json();
    };

    // Fetch data based on selected view
    const fetchData = async () => {
        setLoading(true);
        try {
            setError(null);

            switch (selectedView) {
                case 'overview':
                    const overviewData = await apiCall('/admin/competencies/overview');
                    setOverview(overviewData);
                    break;

                case 'classes':
                    const params = new URLSearchParams();
                    if (filters.grade) params.append('grade', filters.grade);
                    if (filters.teacher) params.append('teacher_id', filters.teacher);

                    const classData = await apiCall(`/ admin / competencies / classes ? ${params.toString()} `);
                    setClassMeters(classData);
                    break;

                case 'teachers':
                    const teacherData = await apiCall('/admin/competencies/teachers');
                    setTeacherOverview(teacherData);
                    break;

                case 'analytics':
                    const analyticsData = await apiCall('/admin/competencies/analytics');
                    setAnalytics(analyticsData);
                    break;

                default:
                    break;
            }
        } catch (err) {
            setError(`Failed to load data: ${err.message} `);
        } finally {
            setLoading(false);
        }
    };

    // View class details
    const viewClassDetails = (classData) => {
        setSelectedClass(classData);
        setShowClassModal(true);
    };

    // View student progress
    const viewStudentProgress = async (studentId) => {
        try {
            setLoading(true);
            const studentData = await apiCall(`/ admin / competencies / student / ${studentId}/progress`);
            setSelectedStudent(studentData);
            setShowStudentModal(true);
        } catch (err) {
            setError(`Failed to load student data: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({ grade: '', teacher: '', search: '' });
    };

    // Show notification - matching TeacherView.js
    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 5000);
    };

    // Get progress color
    const getProgressColor = (percentage) => {
        if (percentage >= 80) return 'text-green-600 bg-green-100';
        if (percentage >= 60) return 'text-yellow-600 bg-yellow-100';
        if (percentage >= 40) return 'text-orange-600 bg-orange-100';
        return 'text-red-600 bg-red-100';
    };

    // Derive gap status label from percentage (Critical > 50%, Moderate > 25%, else Minor)
    const getGapStatus = (percentage) => {
        if (percentage > 50) return 'Critical';
        if (percentage > 25) return 'Moderate';
        return 'Minor';
    };

    // Handle competency gaps filter changes
    const handleGapsFilterChange = (key, value) => {
        setGapsFilters(prev => ({ ...prev, [key]: value }));
    };

    // Get progress bar color
    const getProgressBarColor = (percentage) => {
        if (percentage >= 80) return 'bg-green-500';
        if (percentage >= 60) return 'bg-yellow-500';
        if (percentage >= 40) return 'bg-orange-500';
        return 'bg-red-500';
    };

    // StatsCard component - exactly like TeacherView.js
    const StatsCard = ({ title, value, icon: Icon, color, bgColor, subtitle }) => (
        <div
            className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
            <div className="p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500">{title}</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
                        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
                    </div>
                    <div className="p-3 rounded-full" style={{ backgroundColor: bgColor }}>
                        <Icon className="w-6 h-6" style={{ color: color }} />
                    </div>
                </div>
            </div>
        </div>
    );

    // Render overview section
    const renderOverview = () => (
        <div className="space-y-8">
            {/* Competency Meter Card - matching TeacherView.js style */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                <div className="p-6 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                            <BarChart3 className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Competency Progress
                            Overview</h2>
                    </div>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Circular Progress Chart */}
                        <div className="flex flex-col items-center">
                            <div className="relative w-64 h-64 mb-6">
                                <svg className="w-64 h-64 transform -rotate-90" viewBox="0 0 256 256">
                                    {/* Background Circle */}
                                    <circle
                                        cx="128"
                                        cy="128"
                                        r="100"
                                        stroke="#f3f4f6"
                                        strokeWidth="20"
                                        fill="none"
                                    />
                                    {/* Progress Circle */}
                                    <circle
                                        cx="128"
                                        cy="128"
                                        r="100"
                                        stroke="#D67C2C"
                                        strokeWidth="20"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeDasharray={`${(overview?.overall_completion_rate || 0) * 6.28} 628`}
                                        className="transition-all duration-1000 ease-out"
                                    />
                                </svg>

                                {/* Center Content */}
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <div className="text-4xl font-bold text-[#D67C2C] mb-1">
                                        {overview?.overall_completion_rate || 0}%
                                    </div>
                                    <div className="text-gray-500 text-sm">Overall Progress</div>
                                </div>
                            </div>
                        </div>

                        {/* Domain Legend */}
                        <div className="space-y-4">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Domain Progress</h4>
                            {overview?.completion_by_domain && Object.entries(overview.completion_by_domain).map(([domain, percentage]) => (
                                <div key={domain} className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-700">{domain}</span>
                                        <span
                                            className="text-sm font-bold text-gray-900">{percentage.toFixed(1)}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full transition-all duration-1000 ease-out ${getProgressBarColor(percentage)}`}
                                            style={{ width: `${Math.min(percentage, 100)}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Card */}
            <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-[#D67C2C] mt-1" />
                    <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Summary</h3>
                        <p className="text-gray-700 text-sm leading-relaxed">
                            <span className="font-medium">{overview?.total_classes || 0} classes</span>, <span
                                className="font-medium">{overview?.total_students || 0} students</span>.
                            {overview?.completion_by_domain && Object.entries(overview.completion_by_domain).length > 0 && (
                                <>
                                    {' '}Focus on: <span className="font-medium text-[#D67C2C]">
                                        {Object.entries(overview.completion_by_domain)
                                            .sort(([, a], [, b]) => a - b)
                                            .slice(0, 2)
                                            .map(([domain]) => domain)
                                            .join(', ')}
                                    </span>
                                </>
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    // Render classes section
    const renderClasses = () => (
        <div className="space-y-6">
            {/* Filter Section */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
                <div className="p-6 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                            <Filter className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter Classes</h2>
                    </div>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                            <input
                                type="text"
                                placeholder="Search classes..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                            <select
                                value={filters.grade}
                                onChange={(e) => handleFilterChange('grade', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                            >
                                <option value="">All Grades</option>
                                <option value="BV1">Grade BV1</option>
                                <option value="BV2">Grade BV2</option>
                                <option value="BV3">Grade BV3</option>

                                <option value="1">Grade 1</option>
                                <option value="2">Grade 2</option>
                                <option value="3">Grade 3</option>
                                <option value="4">Grade 4</option>
                                <option value="5">Grade 5</option>
                                <option value="6">Grade 6</option>
                                <option value="7">Grade 7</option>
                                <option value="8">Grade 8</option>
                                <option value="9">Grade 9</option>
                                <option value="10">Grade 10</option>
                                <option value="11">Grade 11</option>
                                <option value="12">Grade 12</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Teacher</label>
                            <select
                                value={filters.teacher}
                                onChange={(e) => handleFilterChange('teacher', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                            >
                                <option value="">All Teachers</option>
                            </select>
                        </div>
                    </div>
                    <div className="mt-4 flex gap-3">
                        <button
                            onClick={clearFilters}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Classes Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {classMeters.map((classData) => (
                    <div key={classData.class_id}
                        className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
                        {/* Class Header */}
                        <div className="flex items-center gap-4 mb-6">
                            <div
                                className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                                <Users className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-gray-900">{classData.class_name}</h3>
                                <p className="text-sm text-gray-600">{classData.teacher_name} • {classData.grade}</p>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                        {classData.total_students} Students
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Progress Circle */}
                        <div className="flex justify-center mb-6">
                            <div className="relative w-32 h-32">
                                <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 128 128">
                                    <circle cx="64" cy="64" r="56" stroke="#f3f4f6" strokeWidth="8" fill="none" />
                                    <circle
                                        cx="64"
                                        cy="64"
                                        r="56"
                                        stroke="#D67C2C"
                                        strokeWidth="8"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeDasharray={`${(classData.average_completion_rate || 0) * 3.52} 352`}
                                        className="transition-all duration-1000 ease-out"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <div
                                        className="text-2xl font-bold text-[#D67C2C]">{classData.average_completion_rate || 0}%
                                    </div>
                                    <div className="text-xs text-gray-500">Completion</div>
                                </div>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                                <div
                                    className="text-lg font-bold text-gray-900">{classData.total_competencies_assigned || 0}</div>
                                <div className="text-xs text-gray-600">Competencies</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                                <div className="text-lg font-bold text-gray-900">{classData.total_students || 0}</div>
                                <div className="text-xs text-gray-600">Students</div>
                            </div>
                        </div>

                        {/* Domain Distribution */}
                        {classData.competency_distribution && Object.keys(classData.competency_distribution).length > 0 && (
                            <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Competency Distribution</h4>
                                <div className="flex flex-wrap gap-1">
                                    {Object.entries(classData.competency_distribution).slice(0, 4).map(([domain, count], index) => {
                                        const colors = ['bg-orange-100 text-orange-700', 'bg-red-100 text-red-700', 'bg-yellow-100 text-yellow-700', 'bg-amber-100 text-amber-700'];
                                        return (
                                            <div key={domain}
                                                className={`px-2 py-1 rounded-lg text-xs font-medium ${colors[index % 4]}`}>
                                                <div className="truncate">{domain}</div>
                                                <div className="text-center">{count}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Action Button */}
                        <button
                            onClick={() => viewClassDetails(classData)}
                            className="w-full bg-[#D67C2C] text-white py-2 px-4 rounded-lg hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                        >
                            <Eye className="w-4 h-4" />
                            View Details
                        </button>
                    </div>
                ))}
            </div>

            {classMeters.length === 0 && !loading && (
                <div className="text-center py-12">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-500">No classes found</p>
                    <p className="text-sm text-gray-400">Classes will appear here once competencies are assigned</p>
                </div>
            )}
        </div>
    );

    // Render teachers section
    const renderTeachers = () => (
        <div className="space-y-6">
            {/* Teachers Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {teacherOverview.map((teacher) => (
                    <div key={teacher.teacher_id}
                        className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
                        {/* Teacher Header */}
                        <div className="flex items-center gap-4 mb-6">
                            <div
                                className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                                <User className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-gray-900">{teacher.teacher_name}</h3>
                                <p className="text-sm text-gray-600">{teacher.teacher_email}</p>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                        {teacher.total_classes} Classes
                                    </span>
                                    <span
                                        className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                        {teacher.total_students} Students
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Performance Metrics */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div
                                className="text-center p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl border border-orange-200">
                                <div
                                    className="text-2xl font-bold text-orange-700">{teacher.average_class_completion_rate}%
                                </div>
                                <div className="text-xs text-orange-600">Avg Completion</div>
                            </div>
                            <div
                                className="text-center p-4 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                                <div
                                    className="text-2xl font-bold text-amber-700">{teacher.total_competencies_assigned}</div>
                                <div className="text-xs text-amber-600">Competencies</div>
                            </div>
                        </div>

                        {/* Domain Coverage */}
                        {teacher.competency_coverage_by_domain && Object.keys(teacher.competency_coverage_by_domain).length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Domain Coverage</h4>
                                <div className="space-y-2">
                                    {Object.entries(teacher.competency_coverage_by_domain).slice(0, 3).map(([domain, percentage]) => (
                                        <div key={domain} className="flex items-center justify-between">
                                            <span className="text-xs text-gray-600 truncate flex-1">{domain}</span>
                                            <div className="flex items-center gap-2 ml-2">
                                                <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                                    <div
                                                        className={`h-1.5 rounded-full ${getProgressBarColor(percentage)}`}
                                                        style={{ width: `${Math.min(percentage, 100)}%` }}
                                                    ></div>
                                                </div>
                                                <span
                                                    className="text-xs font-medium text-gray-900 w-8 text-right">{percentage.toFixed(0)}%</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Classes Overview */}
                        {teacher.classes && teacher.classes.length > 0 && (
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Classes</h4>
                                <div className="space-y-2">
                                    {teacher.classes.slice(0, 3).map((classInfo) => (
                                        <div key={classInfo.class_id}
                                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                            <div className="flex-1">
                                                <div
                                                    className="text-sm font-medium text-gray-900 truncate">{classInfo.class_name}</div>
                                                <div
                                                    className="text-xs text-gray-500">{classInfo.grade} • {classInfo.student_count} students
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div
                                                    className="text-sm font-bold text-gray-900">{classInfo.completion_rate}%
                                                </div>
                                                <div
                                                    className="text-xs text-gray-500">{classInfo.competencies_assigned} comp.
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {teacher.classes.length > 3 && (
                                        <div className="text-center">
                                            <span className="text-xs text-gray-500">+{teacher.classes.length - 3} more classes</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {teacherOverview.length === 0 && !loading && (
                <div className="text-center py-12">
                    <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-500">No teachers found</p>
                    <p className="text-sm text-gray-400">Teachers will appear here once they start using
                        competencies</p>
                </div>
            )}
        </div>
    );

    // Render analytics section
    const renderAnalytics = () => (
        <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Target className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Total Assignments</h3>
                            <p className="text-2xl font-bold text-gray-900">{analytics?.total_assignments || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Completed</h3>
                            <p className="text-2xl font-bold text-gray-900">{analytics?.completed_assignments || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                            <Clock className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">In Progress</h3>
                            <p className="text-2xl font-bold text-gray-900">{analytics?.in_progress_assignments || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Success Rate</h3>
                            <p className="text-2xl font-bold text-gray-900">{analytics?.success_rate || 0}%</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recommendations */}
            {analytics?.recommendations && analytics.recommendations.length > 0 && (
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 bg-gray-50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                <Lightbulb className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">AI Recommendations</h2>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="space-y-4">
                            {analytics.recommendations.map((rec, index) => (
                                <div key={index}
                                    className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                                    <div
                                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${rec.priority === 'high' ? 'bg-red-500' :
                                            rec.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                                            }`}>
                                        {index + 1}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-gray-900 mb-1">{rec.title}</h4>
                                        <p className="text-sm text-gray-700">{rec.description}</p>
                                        {rec.action && (
                                            <button
                                                className="mt-2 text-sm text-[#D67C2C] hover:text-[#c06a25] font-medium">
                                                {rec.action}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Competency Gaps */}
            {analytics?.competency_gaps && analytics.competency_gaps.length > 0 && (
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 bg-gray-50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                <AlertCircle className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Competency Gaps
                                Analysis</h2>
                        </div>
                    </div>
                    <div className="p-6">
                        {/* Gaps Filter Bar */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Domain</label>
                                <select
                                    value={gapsFilters.domain}
                                    onChange={(e) => handleGapsFilterChange('domain', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                >
                                    <option value="">All Domains</option>
                                    {[...new Set(analytics.competency_gaps.map(g => g.domain))].map(domain => (
                                        <option key={domain} value={domain}>{domain}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                <select
                                    value={gapsFilters.status}
                                    onChange={(e) => handleGapsFilterChange('status', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                >
                                    <option value="">All Statuses</option>
                                    <option value="Critical">Critical</option>
                                    <option value="Moderate">Moderate</option>
                                    <option value="Minor">Minor</option>
                                </select>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gap
                                            %
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affected
                                            Students
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {analytics.competency_gaps
                                        .filter(gap => !gapsFilters.domain || gap.domain === gapsFilters.domain)
                                        .filter(gap => !gapsFilters.status || getGapStatus(gap.gap_percentage) === gapsFilters.status)
                                        .map((gap, index) => (
                                        <tr key={index}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{gap.domain}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{gap.gap_percentage}%</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{gap.affected_students}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${gap.gap_percentage > 50 ? 'bg-red-100 text-red-800' :
                                                    gap.gap_percentage > 25 ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-green-100 text-green-800'
                                                    }`}>
                                                    {getGapStatus(gap.gap_percentage)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Performance Trends */}
            {analytics?.performance_trends && analytics.performance_trends.length > 0 && (
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 bg-gray-50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Performance Trends</h2>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {analytics.performance_trends.map((trend, index) => (
                                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="font-semibold text-gray-900">{trend.period}</h4>
                                        <div
                                            className={`flex items-center gap-1 ${trend.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {trend.change >= 0 ? <ArrowUp className="w-4 h-4" /> :
                                                <ArrowDown className="w-4 h-4" />}
                                            <span className="text-sm font-medium">{Math.abs(trend.change)}%</span>
                                        </div>
                                    </div>
                                    <p className="text-2xl font-bold text-gray-900">{trend.completion_rate}%</p>
                                    <p className="text-sm text-gray-600">Completion Rate</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {!analytics && !loading && (
                <div className="text-center py-12">
                    <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-500">No analytics data available</p>
                    <p className="text-sm text-gray-400">Analytics will appear here once there's sufficient data</p>
                </div>
            )}
        </div>
    );

    // Loading state
    if (loading && !overview && !classMeters.length) {
        return (
            <>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="flex justify-center items-center min-h-96">
                        <div className="text-center">
                            <div
                                className="w-8 h-8 text-[#D67C2C] animate-spin rounded-full border-4 border-orange-500 border-t-transparent mx-auto"></div>
                            <p className="mt-4 text-gray-600 font-medium">Loading competency data...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {/* Class Detail Modal */}
            {showClassModal && selectedClass && (
                <div
                    className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex justify-center items-start p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl mt-8 mb-8">
                        {/* Modal Header */}
                        <div
                            className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
                            <div className="flex items-center gap-4">
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-orange-500 to-red-600 shadow-lg">
                                    <Users className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900">{selectedClass.class_name}</h3>
                                    <p className="text-gray-600">{selectedClass.teacher_name} • {selectedClass.grade}</p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                                            {selectedClass.total_students} Students
                                        </span>
                                        <span
                                            className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                                            {selectedClass.total_competencies_assigned} Competencies
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowClassModal(false)}
                                className="p-3 hover:bg-white hover:bg-opacity-50 rounded-xl transition-colors"
                            >
                                <X className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>
                        <div className="p-6 max-h-[70vh] overflow-y-auto">
                            {/* Class Overview Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div
                                    className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center shadow-md">
                                            <TrendingUp className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <div
                                                className="text-2xl font-bold text-orange-700">{selectedClass.average_completion_rate}%
                                            </div>
                                            <div className="text-sm text-orange-600">Average Completion</div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-200">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center shadow-md">
                                            <Users className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <div
                                                className="text-2xl font-bold text-amber-700">{selectedClass.total_students}</div>
                                            <div className="text-sm text-amber-600">Total Students</div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 border border-red-200">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center shadow-md">
                                            <Target className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <div
                                                className="text-2xl font-bold text-red-700">{selectedClass.total_competencies_assigned}</div>
                                            <div className="text-sm text-red-600">Competencies</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Student Progress List */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-lg font-bold text-gray-900">Student Progress</h4>
                                    <div className="text-sm text-gray-500">
                                        {selectedClass.students?.length || 0} students
                                    </div>
                                </div>
                                <div className="space-y-3 max-h-80 overflow-y-auto">
                                    {selectedClass.students && selectedClass.students.map((student, index) => (
                                        <div key={student.student_id}
                                            className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                                            <div className="flex items-center gap-4">
                                                <div
                                                    className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold">
                                                    {index + 1}
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-900">{student.student_name}</p>
                                                    <p className="text-sm text-gray-600">{student.student_email}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="text-right">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-16 bg-gray-200 rounded-full h-2">
                                                            <div
                                                                className={`h-2 rounded-full ${getProgressBarColor(student.progress_percentage)}`}
                                                                style={{ width: `${Math.min(student.progress_percentage, 100)}%` }}
                                                            ></div>
                                                        </div>
                                                        <span
                                                            className="text-sm font-bold text-gray-900 w-12">{student.progress_percentage}%</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">{student.completed_competencies}/{student.total_competencies} completed</p>
                                                </div>
                                                <button
                                                    onClick={() => viewStudentProgress(student.student_id)}
                                                    className="p-2 text-gray-400 hover:text-[#D67C2C] hover:bg-orange-50 rounded-xl transition-colors"
                                                >
                                                    <Eye className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Student Progress Modal */}
            {showStudentModal && selectedStudent && (
                <div
                    className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex justify-center items-start p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mt-8 mb-8">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                    <User className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">{selectedStudent.student_name}</h3>
                                    <p className="text-sm text-gray-600">{selectedStudent.student_email}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowStudentModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        <div className="p-6 max-h-96 overflow-y-auto">
                            <div className="space-y-4">
                                {selectedStudent.competencies && selectedStudent.competencies.map((comp) => (
                                    <div key={comp.competency_id}
                                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-medium text-gray-900">{comp.competency_name}</p>
                                            <p className="text-sm text-gray-600">{comp.domain}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {comp.status === 'completed' ? (
                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                            ) : comp.status === 'in_progress' ? (
                                                <Clock className="w-5 h-5 text-yellow-500" />
                                            ) : (
                                                <AlertCircle className="w-5 h-5 text-gray-400" />
                                            )}
                                            <span className="text-sm font-medium capitalize">{comp.status}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

                        {/* Notification - exactly like TeacherView.js */}
                        {notification && (
                            <div
                                className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-500 text-white' :
                                    notification.type === 'error' ? 'bg-red-500 text-white' :
                                        'bg-blue-500 text-white'
                                    }`} style={{ animation: 'slideInRight 0.5s ease-out' }}>
                                {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
                                    notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> :
                                        <AlertCircle className="w-5 h-5" />}
                                <span className="font-[poppins]">{notification.message}</span>
                                <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Header - exactly like TeacherView.js */}
                        <div className="pt-8 pb-8 text-center relative overflow-hidden">
                            <div className="relative z-10">
                                <div>
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                                        style={{ backgroundColor: '#D67C2C' }}>
                                        <Target className="w-8 h-8 text-white" />
                                    </div>
                                    <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                                        Competency Management Dashboard
                                    </h1>
                                    <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                                        Monitor and analyze competency progress across your institution with real-time
                                        insights
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Dashboard Cards - exactly like TeacherView.js */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                            <StatsCard
                                title="Total Competencies"
                                value={overview?.total_competencies_available || 0}
                                icon={Target}
                                color={colors.blue}
                                bgColor={`${colors.blue}10`}
                            />
                            <StatsCard
                                title="Active Students"
                                value={overview?.total_students || 0}
                                subtitle="Enrolled students"
                                icon={Users}
                                color={colors.green}
                                bgColor={`${colors.green}10`}
                            />
                            <StatsCard
                                title="Active Classes"
                                value={overview?.total_classes || 0}
                                subtitle="Different classes"
                                icon={BookOpen}
                                color={colors.purple}
                                bgColor={`${colors.purple}10`}
                            />
                            <StatsCard
                                title="Overall Progress"
                                value={`${overview?.overall_completion_rate || 0}%`}
                                subtitle={`${overview?.overall_completion_rate >= 70 ? 'Excellent' : 'Needs Focus'}`}
                                icon={TrendingUp}
                                color={colors.orange}
                                bgColor={`${colors.orange}10`}
                            />
                        </div>

                        {/* Navigation Tabs */}
                        <div className="mb-8">
                            <div className="border-b border-gray-200">
                                <nav className="-mb-px flex space-x-8">
                                    {[
                                        { id: 'overview', label: 'Overview', icon: BarChart3 },
                                        { id: 'classes', label: 'Classes', icon: Users },
                                        { id: 'teachers', label: 'Teachers', icon: User },
                                        { id: 'analytics', label: 'Analytics', icon: Activity }
                                    ].map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setSelectedView(tab.id)}
                                            className={`group inline-flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${selectedView === tab.id
                                                ? 'border-[#D67C2C] text-[#D67C2C]'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            <tab.icon className="w-4 h-4" />
                                            {tab.label}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="min-h-96">
                            {selectedView === 'overview' && renderOverview()}
                            {selectedView === 'classes' && renderClasses()}
                            {selectedView === 'teachers' && renderTeachers()}
                            {selectedView === 'analytics' && renderAnalytics()}
                        </div>
                    </div>
                </div>
            </>
        </>
    );
};

export default CompetencyManagement;